///
/// EarthBrowser
///
/// Created by Matt Giger on 11/17/2025
/// Copyright (c) 2025 Geodata Labs
///

import { type Perspective } from "@stovokor/camera/Perspective"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type color } from "@stovokor/math/color"
import { type matrix } from "@stovokor/math/matrix"
import { vec2 } from "@stovokor/math/vector"
import { type Geometry } from "@stovokor/shader/Geometry"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * Material interface extended with arrow animation properties
 */
export interface PolylineMaterial extends Material {
	color: color
	arrowCount?: number
	arrowSpeed?: number
}

// Animation start time for relative time calculation
const animationStartTime = Date.now()

/**
 * Renderer for polylines with screen-space constant width
 * Supports animated arrow patterns flowing along the line
 *
 * Note: The shader uses eye-space calculations for robustness,
 * so modelViewProjection is not needed (only modelView + projection separately)
 */
export class PolylineRenderer extends Renderer {
	public modelView = new UniformDynamic<matrix>()
	public projection = new UniformDynamic<matrix>()
	public viewport = new UniformDynamic<vec2>()
	public nearPlane = new UniformDynamic<number>()
	public time = new UniformDynamic<number>()
	public arrowCount = new UniformDynamic<number>()
	public arrowSpeed = new UniformDynamic<number>()
	public color = new UniformDynamic<color>()

	private material: PolylineMaterial

	constructor(
		context: StovokorContext,
		geometry: Geometry,
		material: PolylineMaterial,
		depthWrite: boolean = true,
		polygonOffset?: [number, number]
	) {
		super(context, new ShaderSourceDefinition("polyline"), geometry, [material])
		this.material = material

		// Initialize arrow animation defaults
		this.time.value = 0
		this.arrowCount.value = material.arrowCount ?? 0
		this.arrowSpeed.value = material.arrowSpeed ?? 0.5

		// Overlays leave depth writes off so later transparent objects don't
		// self-occlude against them; otherwise the pass default applies
		if (!depthWrite) {
			this.drawState.depthWrite = false
		}
		// The line quads are triangles, so polygon offset biases their depth
		// like any fill (the shader writes NDC z with w = 1; the offset is
		// applied in window space after that)
		if (polygonOffset !== undefined) {
			this.drawState.polygonOffset = polygonOffset
		}
	}

	protected stateUpdate(state: RenderState): void {
		this.modelView.value = state.modelView
		this.projection.value = state.projection
		this.viewport.value = new vec2(state.width, state.height)
		this.nearPlane.value = (state.camera.projection as Perspective).near ?? 0.01

		// Update time for animation (use relative time in seconds since app start)
		this.time.value = (Date.now() - animationStartTime) / 1000

		// Update arrow settings from material
		this.arrowCount.value = this.material.arrowCount ?? 0
		this.arrowSpeed.value = this.material.arrowSpeed ?? 0.5

		// Color is dynamic: updateColor replaces the material's color object
		// (e.g. per-frame fades), so a static binding captured at setup would
		// keep reading the original instance forever
		this.color.value = this.material.color
	}
}
